import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { 
  Search, 
  Download, 
  Trash2,
  ScanLine,
  CheckCircle,
  XCircle,
  Clock,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ExportMenu } from '@/components/ExportMenu'
import { cn } from '@/utils/cn'

import { useScanStore } from '@/stores/scanStore'
import { useUIStore } from '@/stores/uiStore'
import { 
  formatDate, 
  formatRelativeTime, 
  formatRepositoryName,
} from '@/utils/formatters'
import scanService from '@/services/scans'
import type { ScanFilters } from '@/types/scan'

const statusOptions = [
  { value: 'all', label: 'All Statuses', count: 0 },
  { value: 'completed', label: 'Completed', count: 0 },
  { value: 'running', label: 'Running', count: 0 },
  { value: 'failed', label: 'Failed', count: 0 },
  { value: 'pending', label: 'Pending', count: 0 },
]

const severityOptions = [
  { value: 'all', label: 'All Severities' },
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
]

export function ScanHistoryPage() {
  const { scans, setScans, setFilters, sort, setSort, isLoading, setLoading } = useScanStore()
  const { addToast } = useUIStore()
  
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedSeverity, setSelectedSeverity] = useState('all')
  const [dateRange, setDateRange] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  
  const itemsPerPage = 10

  // Load scans on component mount and when filters change
  useEffect(() => {
    const loadScans = async () => {
      setLoading(true)
      
      try {
        const scanFilters: ScanFilters = {}
        
        if (selectedStatus !== 'all') {
          scanFilters.status = [selectedStatus]
        }
        
        if (selectedSeverity !== 'all') {
          scanFilters.severity = [selectedSeverity]
        }
        
        if (dateRange !== 'all') {
          const now = new Date()
          const daysAgo = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90
          const fromDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000)
          scanFilters.date_from = fromDate.toISOString()
        }

        if (searchTerm) {
          scanFilters.repository = searchTerm
        }

        const response = await scanService.getScans({
          page: currentPage,
          per_page: itemsPerPage,
          filters: scanFilters,
          sort,
        })
        
        setScans(response.items)
        setTotalPages(response.total_pages)
        setFilters(scanFilters)
      } catch (error) {
        console.error('Failed to load scans:', error)
        addToast({
          title: 'Failed to Load Scans',
          description: 'Could not retrieve scan history. Please try again.',
          type: 'error',
        })
      } finally {
        setLoading(false)
      }
    }

    loadScans()
  }, [currentPage, selectedStatus, selectedSeverity, dateRange, searchTerm, sort, setScans, setFilters, setLoading, addToast])

  // Update status counts
  const statusCounts = useMemo(() => {
    const counts = { all: scans.length, completed: 0, running: 0, failed: 0, pending: 0 }
    scans.forEach(scan => {
      counts[scan.status as keyof typeof counts]++
    })
    return counts
  }, [scans])

  const handleSort = (field: string) => {
    const newDirection = sort.field === field && sort.direction === 'desc' ? 'asc' : 'desc'
    setSort({ field, direction: newDirection })
  }

  const handleDeleteScan = async (scanId: string) => {
    if (!confirm('Are you sure you want to delete this scan? This action cannot be undone.')) {
      return
    }

    try {
      await scanService.deleteScan(scanId)
      setScans(scans.filter(scan => scan.id !== scanId))
      addToast({
        title: 'Scan Deleted',
        description: 'The scan has been successfully deleted.',
        type: 'success',
      })
    } catch (error) {
      addToast({
        title: 'Delete Failed',
        description: 'Could not delete the scan. Please try again.',
        type: 'error',
      })
    }
  }


  const getSortIcon = (field: string) => {
    if (sort.field !== field) return <ArrowUpDown className="h-4 w-4" />
    return sort.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />
      case 'running':
        return <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />
      default:
        return <ScanLine className="h-4 w-4 text-gray-400" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
          Scan History
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          View and manage all your security scans
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters & Search</CardTitle>
          <CardDescription>
            Filter and search through your scan history
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search repositories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="running">Running</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>

            {/* Severity Filter */}
            <select
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {severityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Date Range */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="all">All Time</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Status Overview */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        {statusOptions.map((status) => {
          const count = statusCounts[status.value as keyof typeof statusCounts] || 0
          return (
            <Card
              key={status.value}
              className={cn(
                'cursor-pointer transition-colors',
                selectedStatus === status.value && 'ring-2 ring-blue-500'
              )}
              onClick={() => setSelectedStatus(status.value)}
            >
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold">{count}</div>
                <div className="text-xs text-muted-foreground">{status.label}</div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Scans Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Scan Results</CardTitle>
              <CardDescription>
                {scans.length} scans found
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="h-12 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 animate-pulse" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3 animate-pulse" />
                  </div>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20 animate-pulse" />
                </div>
              ))}
            </div>
          ) : scans.length === 0 ? (
            <div className="text-center py-12">
              <ScanLine className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No scans found
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || selectedStatus !== 'all' || selectedSeverity !== 'all' || dateRange !== 'all'
                  ? 'Try adjusting your filters to see more results.'
                  : 'Start your first security scan to see results here.'}
              </p>
              <Button asChild>
                <Link to="/">Start New Scan</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Table Header */}
              <div className="grid grid-cols-6 gap-4 pb-3 border-b text-sm font-medium text-gray-500 dark:text-gray-400">
                <button
                  onClick={() => handleSort('repository_url')}
                  className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-white"
                >
                  Repository {getSortIcon('repository_url')}
                </button>
                <button
                  onClick={() => handleSort('created_at')}
                  className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-white"
                >
                  Date {getSortIcon('created_at')}
                </button>
                <div>Status</div>
                <button
                  onClick={() => handleSort('risk_score')}
                  className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-white"
                >
                  Risk Score {getSortIcon('risk_score')}
                </button>
                <div>Findings</div>
                <div>Actions</div>
              </div>

              {/* Table Rows */}
              {scans.map((scan) => (
                <div
                  key={scan.id}
                  className="grid grid-cols-6 gap-4 py-3 border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <div>
                    <div className="font-medium text-sm">
                      {formatRepositoryName(scan.repository_url)}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      {scan.branch}
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-sm">{formatDate(scan.created_at)}</div>
                    <div className="text-xs text-gray-500">
                      {formatRelativeTime(scan.created_at)}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {getStatusIcon(scan.status)}
                    <Badge
                      variant={
                        scan.status === 'completed' ? 'success' :
                        scan.status === 'failed' ? 'destructive' :
                        scan.status === 'running' ? 'warning' :
                        'secondary'
                      }
                      className="text-xs"
                    >
                      {scan.status}
                    </Badge>
                  </div>
                  
                  <div>
                    {scan.summary ? (
                      <div className="text-sm font-medium">
                        {scan.summary.risk_score.toFixed(1)}/10
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">--</span>
                    )}
                  </div>
                  
                  <div>
                    {scan.summary ? (
                      <div className="flex items-center gap-1">
                        <span className="text-sm">{scan.summary.total_findings}</span>
                        {scan.summary.critical_count > 0 && (
                          <Badge variant="critical" className="text-xs px-1">
                            {scan.summary.critical_count}C
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">--</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/scan/${scan.id}`}>
                        <Eye className="h-4 w-4" />
                      </Link>
                    </Button>
                    
                    <ExportMenu
                      scanId={scan.id}
                      isCompleted={scan.status === 'completed'}
                      variant="button"
                      size="sm"
                    />
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteScan(scan.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}